import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Stack, useTheme } from "@mui/material";
import Modal from "components/basic/modal";
import Scrollbar from "components/basic/scrollbar";
import Typography from "components/basic/typography";
import LoadingSpinner from "components/basic/loadingSpinner";
import { useSnack } from "hooks/useSnack";

import {
  useMessageActions,
  useMessageableCounterparts,
  useMySide,
} from "../../messagesApi";
import { MessageableCounterpart } from "../../types";

interface NewConversationDialogProps {
  onClose: () => void;
  onOpenConversation: (conversationId: number) => void;
}

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({
  onClose,
  onOpenConversation,
}) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("messages");
  const mySide = useMySide();
  const { counterparts, loading } = useMessageableCounterparts();
  const { start } = useMessageActions();
  const [startingId, setStartingId] = useState<number | null>(null);

  const handlePick = async (counterpart: MessageableCounterpart) => {
    if (counterpart.conversationId) {
      onOpenConversation(counterpart.conversationId);
      return;
    }
    setStartingId(counterpart.otherPartyId);
    const result = await start(counterpart.otherPartyId);
    setStartingId(null);
    if (!result.ok || !result.conversationId) {
      showSnack(result.error ?? t("snack.start_failed"), "error");
      return;
    }
    onOpenConversation(result.conversationId);
  };

  const emptyCopy =
    mySide === "user" ? t("dialog.empty_user") : t("dialog.empty_vendor");

  return (
    <Modal onClose={onClose}>
      {/* A flat 360px floor is wider than a phone once the Modal's own padding
          is added, but dropping it entirely shrinks the dialog to its longest
          name. */}
      {/* The Modal panel is shrink-to-fit, so a percentage here has no base to
          resolve against and the dialog collapses to its longest name. */}
      <Box
        sx={{
          width: { xs: "min(320px, 100vw - 64px)", sm: 360 },
          maxWidth: 480,
        }}
      >
        <Typography
          variant="h4"
          style={{ fontWeight: "bold", marginBottom: 16 }}
        >
          {t("new_message")}
        </Typography>

        {loading ? (
          <LoadingSpinner />
        ) : counterparts.length === 0 ? (
          <Typography
            variant="body2"
            style={{ color: theme.palette.text.secondary }}
          >
            {emptyCopy}
          </Typography>
        ) : (
          <Scrollbar sx={{ maxHeight: 400 }} showHint={false}>
            <Stack>
              {counterparts.map((counterpart) => (
                <Box
                  key={counterpart.otherPartyId}
                  display="flex"
                  alignItems="center"
                  gap={theme.spacing(2)}
                  px={1}
                  py={1}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    opacity: startingId === counterpart.otherPartyId ? 0.5 : 1,
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                  onClick={() => handlePick(counterpart)}
                >
                  <Avatar
                    src={counterpart.avatarUrl ?? undefined}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                  />
                  <Typography variant="body1" style={{ flexGrow: 1 }}>
                    {counterpart.name}
                  </Typography>
                  {counterpart.conversationId && (
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("dialog.existing")}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Scrollbar>
        )}
      </Box>
    </Modal>
  );
};

export default NewConversationDialog;
